import React, { useState, useEffect, useRef } from 'react'
import { sendUserAttributeVerificationCode, confirmUserAttribute } from 'aws-amplify/auth'
import { Button, TextField, Text, Heading } from '@aws-amplify/ui-react'
import styled from 'styled-components'

interface EmailVerificationProps {
  email: string
  onVerificationComplete: () => void
  onCancel?: () => void
}

export const EmailVerification: React.FC<EmailVerificationProps> = ({ email, onVerificationComplete, onCancel }) => {
  const [verificationCode, setVerificationCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [isResending, setIsResending] = useState(false)
  const [resendMessage, setResendMessage] = useState('')
  const emailSentRef = useRef(false)

  const validateCode = (code: string) => {
    return /^\d{6}$/.test(code)
  }

  const sendInitialVerificationEmail = async () => {
    if (emailSentRef.current) {
      return
    }

    emailSentRef.current = true
    setError('')

    try {
      await sendUserAttributeVerificationCode({
        userAttributeKey: 'email',
      })
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  useEffect(() => {
    sendInitialVerificationEmail()
  }, [emailSentRef.current])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateCode(verificationCode)) {
      setError('Please enter a valid 6-digit code')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      await confirmUserAttribute({
        userAttributeKey: 'email',
        confirmationCode: verificationCode,
      })
      onVerificationComplete()
    } catch (err: unknown) {
      setError(getErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendCode = async () => {
    setIsResending(true)
    setError('')
    setResendMessage('')

    try {
      await sendUserAttributeVerificationCode({
        userAttributeKey: 'email',
      })
      setResendMessage('Verification code sent to your email')
    } catch (err: unknown) {
      setError(getErrorMessage(err))
    } finally {
      setIsResending(false)
    }
  }

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
    setVerificationCode(value)
    if (error) setError('')
  }

  const getErrorMessage = (err: unknown): string => {
    if (err instanceof Error) {
      if (err.name === 'CodeMismatchException') {
        return 'Invalid verification code. Please try again.'
      }
      if (err.name === 'ExpiredCodeException') {
        return 'Verification code has expired. Please request a new one.'
      }
      if (err.name === 'LimitExceededException') {
        return 'Too many attempts. Please wait before trying again.'
      }
      return err.message
    }
    return 'An error occurred during verification'
  }

  return (
    <Container>
      <ContentStack>
        <HeaderStack>
          <Heading level={4}>Verify your email</Heading>
          <Text>{`We've sent a verification code to ${email}`}</Text>
        </HeaderStack>

        <form onSubmit={handleSubmit}>
          <FormStack>
            <div>
              <TextField
                id="verification-code"
                label="Confirmation Code"
                placeholder="Enter your code"
                value={verificationCode}
                onChange={handleCodeChange}
                maxLength={6}
              />
              {error && <ErrorText>{error}</ErrorText>}
              {resendMessage && <SuccessText>{resendMessage}</SuccessText>}
            </div>

            <Button type="submit" variation="primary" isDisabled={!validateCode(verificationCode) || isLoading}>
              {isLoading ? 'Verifying...' : 'Verify Email'}
            </Button>

            <ButtonRow>
              <Button type="button" onClick={handleResendCode} isDisabled={isResending} isFullWidth>
                {isResending ? 'Sending...' : 'Resend code'}
              </Button>
              {onCancel && (
                <Button type="button" variation="link" onClick={onCancel} isDisabled={isLoading}>
                  Cancel
                </Button>
              )}
            </ButtonRow>
          </FormStack>
        </form>
      </ContentStack>
    </Container>
  )
}

const Container = styled.div`
  width: 100%;
`

const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const HeaderStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  text-align: left;
  margin-bottom: 16px;
`

const FormStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ButtonRow = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
`

const ErrorText = styled.div`
  margin-top: 0.8rem;
  display: block;
  color: #e44343;
  font-size: 0.875rem;
`

const SuccessText = styled.div`
  margin-top: 0.8rem;
  display: block;
  color: #48a395;
  font-size: 0.875rem;
`
